import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Paper, Typography } from '@mui/material';
import { spaces, palette, customSizes, borderRadius } from '../../../assets/styles/themes/tokens';
import { MindItem } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import { useSelector } from 'react-redux';
import { StoreType } from '../../../reducers';
import { selectMindItem } from '../../../reducers/mindItems.selectors';
import { useReaderContext } from '../../Reader/ReaderContext';
import { Link } from 'found';
import { routes } from '../../../constants';
import { Hint } from '../../Hint';
import { noop } from 'lodash';
import useScreenSize from '../../../utils/useScreenSize';

const PagePreviewList = () => {
  const { t } = useTranslation();
  const { currentPage, mindItemId, fullScreenMode, onboardingStepCounter } = useReaderContext();
  const mindItem = useSelector(
    (state: StoreType) => selectMindItem(state, mindItemId)
  ) as MindItem<'document'> | MindItem<'read-only'>;
  const { isLargeScreen } = useScreenSize();
  if (!mindItem) return null;

  const NumberedPages = () => {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}>
        {
          mindItem.pages.map((page, i) => {
            const index = i + 1;
            const isCurrentPage = currentPage === index;

            let component = (
              <Link
                to={{
                  pathname: `/${routes.reader.root}/${mindItem.id}`,
                  query: { page: `${index}` }
                }}
                key={index}
              >
                <Box 
                  key={index} 
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    gap: spaces.xxs,
                    marginBottom: spaces.sm,
                  }}
                >
                  <Box
                    component='img'
                    sx={{
                      width: '100%',
                      height: '100%',
                      border: isCurrentPage 
                        ? `1px solid ${palette.primary.main}` 
                        : `1px solid ${palette.elevation.outlined}`,
                      borderRadius: borderRadius.xs,
                    }}
                    src={page.w58}
                  />
                  <Typography key={index} variant='caption' color={palette.text.secondary}>
                    {index}
                  </Typography>
                </Box>
              </Link>
            );

            if (index === 1 && isLargeScreen) {
              component = <Hint
                width={288}
                open={onboardingStepCounter?.step === 2}
                key={index}
                placement='left'
                onClose={onboardingStepCounter?.dismiss || noop}
                description={t('Here you can navigate through the pages of the unit.')}
                actions={{
                  primary: {
                    label: t('Continue'),
                    onClick: onboardingStepCounter ? onboardingStepCounter.incrementStep : noop,
                    buttonProps: {
                      variant: 'text',
                      size: 'small',
                      sx: {
                        color: palette.primary.contrastText,
                      }
                    }
                  },
                }}
                bottomLeftText='2/3'
              >
                <Box>
                  {component}
                </Box>
              </Hint>;
            }

            return component;
          })
        }
      </Box>
    );
  };

  return (
    <Paper 
      elevation={2}
      sx={{
        borderRadius: spaces.sm,
        color: palette.text.primary,
        width: customSizes.pagePreviewList.width,
        marginY: customSizes.readerPage.paddingY,
        display: fullScreenMode ? 'none' : 'flex',
        flexDirection: 'column'
      }}
    >
      <Box
        sx={{
          paddingTop: spaces.md,
          paddingBottom: spaces.sm,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
        data-testid='page-preview-num-of-pages'>
        <Typography variant='overline'>
          {mindItem.pages.length} {mindItem.pages.length > 1 ? t('Pages') : t('Page')}
        </Typography>
      </Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        overflowY: 'scroll',
      }}>
        <NumberedPages />
      </Box>
    </Paper>
  );
};

export default PagePreviewList;
